import { useEffect } from "react";

import RulerControl from "mapbox-gl-controls/lib/ruler";
import { UNIT_LABELS, UnitSystem } from "types/units";

import { useLengthConversion } from "../../../hooks";

const FEET_PER_MILE = 5280;
const METERS_PER_KILOMETER = 1000;

const TURF_UNITS = {
  METERS: "meters",
  FEET: "feet"
};

export function useMapRulerControl(mapbox: mapboxgl.Map | null) {
  const { unitType, getDisplayUnit } = useLengthConversion();

  useEffect(() => {
    if (!mapbox) return;

    const isImperial = unitType === UnitSystem.Imperial;
    const rulerUnits = isImperial ? TURF_UNITS.FEET : TURF_UNITS.METERS;

    const mapRuler = new RulerControl({
      units: rulerUnits,
      labelFormat: (n) => {
        if (n === 0) return "";

        if (isImperial) {
          // Convert to miles if >= 1 mile
          if (n >= FEET_PER_MILE) {
            const miles = n / FEET_PER_MILE;
            return `${miles.toFixed(2)} ${UNIT_LABELS.MILES}`;
          }
          return `${n.toFixed(2)} ${UNIT_LABELS.FEET}`;
        } else {
          // Convert to kilometers if >= 1 km
          if (n >= METERS_PER_KILOMETER) {
            const kilometers = n / METERS_PER_KILOMETER;
            return `${kilometers.toFixed(2)} ${UNIT_LABELS.KILOMETERS}`;
          }
          return `${n.toFixed(2)} ${UNIT_LABELS.METERS}`;
        }
      }
    });

    mapbox.addControl(mapRuler, "top-right");
    mapRuler.button.setAttribute("title", "Ruler");

    return () => {
      try {
        if (mapbox && mapRuler) {
          mapbox.removeControl(mapRuler);
        }
      } catch (error) {
        // Control may have already been removed - silently ignore
      }
    };
  }, [mapbox, unitType, getDisplayUnit]);
}
